import React, { useState, useRef, useEffect, useCallback } from "react"
import {
	sendMessage,
	uploadGroupImage,
	uploadGroupFile,
	GroupMember,
	Group,
} from "firebase/groupsService"
import { API, Endpoints } from "utils/api"
import { notification } from "antd"
import Strings from "utils/strings"

export function useChatInput(
	selectedGroup: Group | null,
	loggedUserId: string,
	myName: string,
	onTypingChange?: (isTyping: boolean) => void,
	onMessageSent?: (text: string) => void
) {
	const [inputText, setInputText] = useState("")
	const [sending, setSending] = useState(false)
	const [mentionQuery, setMentionQuery] = useState<string | null>(null)
	const [mentionStart, setMentionStart] = useState(0)
	const [mentionIndex, setMentionIndex] = useState(0)

	const textareaRef = useRef<HTMLTextAreaElement | null>(null)
	const mentionPickerRef = useRef<HTMLDivElement>(null)
	const imageInputRef = useRef<HTMLInputElement>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [uploading, setUploading] = useState(false)
	const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const onTypingChangeRef = useRef(onTypingChange)
	onTypingChangeRef.current = onTypingChange
	const onMessageSentRef = useRef(onMessageSent)
	onMessageSentRef.current = onMessageSent

	useEffect(() => {
		return () => {
			if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
			onTypingChangeRef.current?.(false)
		}
	}, [selectedGroup?.id])

	useEffect(() => {
		setMentionIndex(0)
	}, [mentionQuery])

	useEffect(() => {
		const active = mentionPickerRef.current?.querySelector<HTMLElement>(
			".mention-picker__item--active"
		)
		active?.scrollIntoView({ block: "nearest", behavior: "smooth" })
	}, [mentionIndex])

	const resetInput = useCallback(() => {
		setInputText("")
		setMentionQuery(null)
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto"
			textareaRef.current.style.overflowY = "hidden"
		}
	}, [])

	const handleImageClick = useCallback(() => {
		imageInputRef.current?.click()
	}, [])

	const handleFileClick = useCallback(() => {
		fileInputRef.current?.click()
	}, [])

	const handleImageFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (!file || !selectedGroup || uploading) return
			e.target.value = ""
			if (!file.type.startsWith("image/")) {
				notification.error({
					message: Strings.routes.invalidFileType,
					description: Strings.routes.onlyImagesAllowed,
					placement: "bottomRight",
					duration: 5,
				})
				return
			}

			setUploading(true)
			try {
				const url = await uploadGroupImage(selectedGroup.id, loggedUserId, file)
				await sendMessage({
					groupId: selectedGroup.id,
					content: "",
					senderId: loggedUserId,
					senderType: "backoffice",
					senderName: myName,
					attachmentUrl: url,
					attachmentType: "image",
				})
				API.post({
					url: Endpoints.uriMessagingNotify(),
					data: {
						groupId: selectedGroup.id,
						groupName: selectedGroup.name,
						senderId: loggedUserId,
						senderName: myName,
						content: "",
						attachmentType: "image",
					},
				}).catch(() => {})
			} finally {
				setUploading(false)
			}
		},
		[selectedGroup, loggedUserId, myName, uploading]
	)

	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (!file || !selectedGroup || uploading) return
			e.target.value = ""

			setUploading(true)
			try {
				const url = await uploadGroupFile(selectedGroup.id, loggedUserId, file)
				await sendMessage({
					groupId: selectedGroup.id,
					content: file.name,
					senderId: loggedUserId,
					senderType: "backoffice",
					senderName: myName,
					attachmentUrl: url,
					attachmentType: "file",
				})
				API.post({
					url: Endpoints.uriMessagingNotify(),
					data: {
						groupId: selectedGroup.id,
						groupName: selectedGroup.name,
						senderId: loggedUserId,
						senderName: myName,
						content: file.name,
						attachmentType: "file",
					},
				}).catch(() => {})
			} finally {
				setUploading(false)
			}
		},
		[selectedGroup, loggedUserId, myName, uploading]
	)

	const handleSend = useCallback(async () => {
		const text = inputText.trim()
		if (!text || !selectedGroup || sending) return

		if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
		onTypingChangeRef.current?.(false)
		setSending(true)
		resetInput()
		try {
			await sendMessage({
				groupId: selectedGroup.id,
				content: text,
				senderId: loggedUserId,
				senderType: "backoffice",
				senderName: myName,
			})
			API.post({
				url: Endpoints.uriMessagingNotify(),
				data: {
					groupId: selectedGroup.id,
					groupName: selectedGroup.name,
					senderId: loggedUserId,
					senderName: myName,
					content: text,
					attachmentType: null,
				},
			}).catch(() => {})
			onMessageSentRef.current?.(text)
		} finally {
			setSending(false)
		}
	}, [inputText, selectedGroup, sending, loggedUserId, myName, resetInput])

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const text = e.target.value
			setInputText(text)

			// Auto-grow
			const el = e.target
			el.style.height = "auto"
			const next = Math.min(el.scrollHeight, 120)
			el.style.height = next + "px"
			el.style.overflowY = el.scrollHeight > 120 ? "auto" : "hidden"

			// Typing indicator
			if (text.trim()) {
				onTypingChangeRef.current?.(true)
				if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
				typingTimerRef.current = setTimeout(() => {
					onTypingChangeRef.current?.(false)
				}, 3000)
			} else {
				if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
				onTypingChangeRef.current?.(false)
			}

			// @mention detection
			const cursor = el.selectionStart ?? text.length
			const before = text.substring(0, cursor)
			const atIdx = before.lastIndexOf("@")

			if (atIdx >= 0) {
				const afterAt = before.substring(atIdx + 1)
				const prevChar = atIdx > 0 ? text[atIdx - 1] : " "
				if (!afterAt.includes(" ") && (prevChar === " " || atIdx === 0)) {
					setMentionQuery(afterAt)
					setMentionStart(atIdx)
					return
				}
			}
			setMentionQuery(null)
		},
		[]
	)

	const insertMention = useCallback(
		(member: GroupMember) => {
			const el = textareaRef.current
			const cursor =
				el?.selectionStart ?? mentionStart + (mentionQuery?.length ?? 0) + 1
			const before = inputText.substring(0, mentionStart)
			const after = inputText.substring(cursor)
			const newText = `${before}@${member.name} ${after}`
			setInputText(newText)
			setMentionQuery(null)
			setTimeout(() => {
				if (el) {
					const pos = before.length + member.name.length + 2
					el.focus()
					el.setSelectionRange(pos, pos)
				}
			}, 0)
		},
		[inputText, mentionStart, mentionQuery]
	)

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (mentionQuery !== null && selectedGroup) {
				const pickerMembers = selectedGroup.members
					.filter((m) => m.id !== loggedUserId)
					.filter((m) => m.type !== "bot" || selectedGroup.hasBotActive)
					.filter((m) =>
						m.name.toLowerCase().includes(mentionQuery.toLowerCase())
					)

				if (e.key === "ArrowDown") {
					e.preventDefault()
					setMentionIndex((i) =>
						pickerMembers.length ? (i + 1) % pickerMembers.length : 0
					)
					return
				}
				if (e.key === "ArrowUp") {
					e.preventDefault()
					setMentionIndex((i) =>
						pickerMembers.length
							? (i - 1 + pickerMembers.length) % pickerMembers.length
							: 0
					)
					return
				}
				if (e.key === "Enter" && pickerMembers[mentionIndex]) {
					e.preventDefault()
					insertMention(pickerMembers[mentionIndex])
					return
				}
				if (e.key === "Escape") {
					setMentionQuery(null)
					return
				}
			}

			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault()
				handleSend()
			}
		},
		[
			mentionQuery,
			selectedGroup,
			loggedUserId,
			mentionIndex,
			insertMention,
			handleSend,
		]
	)

	return {
		inputText,
		sending,
		uploading,
		mentionQuery,
		mentionIndex,
		textareaRef,
		mentionPickerRef,
		imageInputRef,
		fileInputRef,
		handleSend,
		handleInputChange,
		handleKeyDown,
		handleImageClick,
		handleImageFileChange,
		handleFileClick,
		handleFileChange,
		insertMention,
		resetInput,
	}
}
